/**
 * Messaging Page - Chat between Coach and Client (MVP+)
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, MessageCircle, Send, Lock, Loader2, User,
} from "lucide-react";
import {
  useConversations, useConversation, sendMessage,
} from "../hooks/useMessaging";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Toaster, toast } from "sonner";

export default function MessagesPage() {
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const { user } = useAuth();
  const { conversations, mutate: mutateConvos } = useConversations();
  const { messages, mutate: mutateMessages } = useConversation(partnerId);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !partnerId) return;
    setSending(true);
    try {
      await sendMessage(partnerId, newMessage.trim());
      setNewMessage("");
      mutateMessages();
      mutateConvos();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  // If no partner selected, show conversation list
  if (!partnerId) {
    return (
      <div className="min-h-screen bg-zinc-950 pb-20">
        <Toaster position="top-right" richColors />
        <header className="sticky top-0 z-40 glass border-b border-zinc-800">
          <div className="max-w-lg mx-auto px-4 flex items-center justify-between h-14">
            <Button variant="ghost" size="sm" onClick={() => navigate(user?.role === "admin" ? "/admin" : "/dashboard")} data-testid="back-btn" className="text-zinc-400 hover:text-white -ml-2">
              <ArrowLeft className="w-5 h-5 mr-1" /> Retour
            </Button>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-indigo-400" />
              <span className="font-heading font-bold text-white">MESSAGES</span>
            </div>
            <div className="w-16" />
          </div>
        </header>

        <main className="max-w-lg mx-auto px-4 py-6">
          {conversations.length > 0 ? (
            <div className="space-y-2">
              {conversations.map((c) => (
                <Card
                  key={c.partner_id}
                  className="bg-zinc-900/50 border-zinc-800 hover:border-indigo-400/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/messages/${c.partner_id}`)}
                  data-testid={`conversation-${c.partner_id}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-400/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-white text-sm truncate">{c.partner_name}</p>
                        <span className="text-xs text-zinc-500 flex-shrink-0">
                          {new Date(c.last_message_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 truncate">{c.last_message}</p>
                    </div>
                    {c.unread_count > 0 && (
                      <Badge className="bg-indigo-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center p-0 flex-shrink-0">
                        {c.unread_count}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-12 text-center">
                <MessageCircle className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">Aucune conversation</p>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    );
  }

  // Chat view
  const partner = conversations.find((c) => c.partner_id === partnerId);
  const partnerName = partner?.partner_name || "Conversation";

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4 flex items-center gap-3 h-14">
          <Button variant="ghost" size="icon" onClick={() => navigate("/messages")} data-testid="back-to-conversations" className="text-zinc-400 hover:text-white -ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-8 h-8 bg-indigo-400/10 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-indigo-400" />
          </div>
          <span className="font-heading font-bold text-white text-sm">{partnerName}</span>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto max-w-lg w-full mx-auto px-4 py-4">
        <div className="space-y-3">
          {messages.map((msg) => {
            const isMine = msg.sender_id === user?.id;
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${isMine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                    isMine
                      ? "bg-indigo-500 text-white rounded-br-sm"
                      : "bg-zinc-800 text-zinc-200 rounded-bl-sm"
                  }`}
                  data-testid={`message-${msg.id}`}
                >
                  <p>{msg.content}</p>
                  <p className={`text-xs mt-1 ${isMine ? "text-indigo-200" : "text-zinc-500"}`}>
                    {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </motion.div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input */}
      <div className="sticky bottom-0 glass border-t border-zinc-800">
        <form onSubmit={handleSend} className="max-w-lg mx-auto px-4 py-3 flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Écrire un message..."
            data-testid="message-input"
            className="bg-zinc-900 border-zinc-700 h-11 flex-1"
          />
          <Button
            type="submit"
            disabled={sending || !newMessage.trim()}
            data-testid="send-message-button"
            className="bg-indigo-500 hover:bg-indigo-600 text-white h-11 w-11 p-0 rounded-full"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}

/**
 * Locked messaging placeholder when feature is disabled
 */
export function MessagesLockedPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-sm"
      >
        <div className="w-20 h-20 bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-zinc-500" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-white mb-2">Messagerie Premium</h2>
        <p className="text-zinc-400 mb-6">
          La messagerie directe avec votre coach est incluse dans l'abonnement Premium. Contactez votre coach pour activer cette fonctionnalité.
        </p>
        <Button
          onClick={() => navigate(user?.role === "admin" ? "/admin" : "/dashboard")}
          data-testid="back-to-dashboard"
          className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-full px-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      </motion.div>
    </div>
  );
}
